import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { userActions } from '@akademiasaas/shared';
import { useAppDispatch } from '~/initializeStore';
import { Callout } from '~/components/ui';
import AuthCard, { AuthHeading } from '../../layout/AuthCard';
import EmailForm from '../../form/EmailForm';
import SentResult from '../../form/SentResult';
import { authErrorKind } from '../../form/authErrors';

/** Adres, na który wysłano link — `SignWithLink` czyta go, gdy link otwarto w tej przeglądarce. */
export const SIGN_IN_EMAIL_STORAGE_KEY = 'emailForSignIn';

const LoginByEmail: FunctionComponent = () => {
  const { i18n, t } = useTranslation(['auth']);
  const dispatch = useAppDispatch();
  const history = useHistory();
  // Odczyt raz, przy wejściu: adres z logowania i kod błędu z `SignWithLink` (wygasły link).
  const { search } = useLocation();
  const [params] = useState(() => new URLSearchParams(search));
  const [email, setEmail] = useState(() => params.get('email')?.replace(' ', '+') ?? '');
  // Od pierwszego renderu, nie po efekcie: inaczej pole dostaje autofokus, komunikat go odbiera,
  // a blur od razu pokazuje błąd pustego pola.
  const [expired, setExpired] = useState(
    () => authErrorKind(params.get('error')) === 'expiredLink'
  );
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    dispatch(userActions.resetErrors());
    if (params.toString()) history.replace({ search: '' });
  }, [dispatch, history, params]);

  const send = async (address: string) => {
    await dispatch(
      userActions.sendLoginLink(
        address,
        `${window.location.origin}/auth/sign-with-link`,
        i18n.language
      )
    );
    window.localStorage.setItem(SIGN_IN_EMAIL_STORAGE_KEY, address);
  };

  const onSubmit = async (address: string) => {
    setBusy(true);
    setEmail(address);
    await send(address);
    setBusy(false);
    setSent(true);
    setExpired(false);
  };

  return (
    <AuthCard
      footer={
        sent ? (
          <Link to="/auth/login">{t('auth:screens.sent.back')}</Link>
        ) : (
          <Link to="/auth/login">{t('auth:screens.link.withPassword')}</Link>
        )
      }
    >
      {sent ? (
        <SentResult
          email={email}
          textKey="auth:screens.link.sentText"
          resendLabel={t('auth:screens.link.resend')}
          onResend={() => send(email)}
          onFixAddress={() => setSent(false)}
        />
      ) : (
        <>
          <AuthHeading title={t('auth:screens.link.title')} lead={t('auth:screens.link.lead')} />
          <EmailForm
            name="loginByLink"
            initialEmail={email}
            submitLabel={t('auth:screens.link.submit')}
            busyLabel={t('auth:screens.link.busy')}
            busy={busy}
            onSubmit={onSubmit}
            notice={
              expired ? (
                <Callout tone="error" title={t('auth:screens.link.expiredTitle')} focusOnMount>
                  {t('auth:screens.link.expiredText')}
                </Callout>
              ) : undefined
            }
          />
        </>
      )}
    </AuthCard>
  );
};

export default LoginByEmail;
